import React, { useMemo } from 'react';
import { ContentContainer, Gap, Text, Title } from '@common/components';
import {
  describeShiftCount,
  buildConfirmationLine,
  isValidSelection,
} from './_bulk-shift-step-set-tiers-service';
import type {
  SelectedGapEntryType,
  Step2SelectionsType,
} from './_bulk-shift-step-types';

export function BulkShiftStepConfirm({
  selectedGaps,
  selections,
}: {
  selectedGaps: SelectedGapEntryType[];
  selections: Step2SelectionsType;
}) {
  const selectedRows = useMemo(
    () => Object.values(selections).filter(isValidSelection),
    [selections]
  );

  const shiftCount = selectedGaps.length;
  const shiftLabel = describeShiftCount(selectedGaps);

  return (
    <ContentContainer
      title="Summary"
      header={
        <Text>
          The selected rates will be applied to {shiftLabel}. Workforce groups
          left unset will be skipped.
        </Text>
      }
    >
      <Gap vertical size="lg" fullWidth align="stretch">
        <Gap vertical size="sm" fullWidth align="stretch">
          <Text>
            <span data-testid="bulk-shift-confirm-shift-count">
              Shifts to update: {shiftCount}
            </span>
          </Text>
          <Text>Workforce groups updated: {selectedRows.length}</Text>
        </Gap>
        {selectedRows.length === 0 ? (
          <Text secondary data-testid="bulk-shift-confirm-summary">
            No workforce groups have a chosen rate. Go back to Step 2 to pick at
            least one rate before applying.
          </Text>
        ) : (
          <Gap vertical size="md" fullWidth align="stretch">
            <Title size="h3">Rates by workforce group</Title>
            {selectedRows.map((selection) => (
              <Text
                key={selection.workforceGroupId}
                dataTestId={`bulk-shift-confirm-line-${selection.workforceGroupId}`}
              >
                {buildConfirmationLine({ selection, shiftCount })}
              </Text>
            ))}
          </Gap>
        )}
      </Gap>
    </ContentContainer>
  );
}
